import { useRequiredFieldState } from '@cashew/hooks';
import React, {
  useEffect,
  useState,
  useCallback,
  ChangeEvent,
  useMemo,
} from 'react';

import { useTranslation } from 'react-i18next';
import Input from '@cashew/input';
import {
  DefaultSectionProps,
  Emirate,
  emirates,
  emiratesAsStrings,
  isEmirate,
  isStringUnknownRecord,
  merchantEndpoint,
  useToasterErrorState,
  useToasterSuccessState,
} from './util';
import OnboardingForm from './OnboardingForm';
import SelectComponent from '../../components/SelectComponent';

const { api } = require('@cashew/utils');

export interface BusinessInfo {
  tradeLicense: string;
  vatNumber: string;
  addressLine1: string;
  addressLine2: string;
  poBox: string;
  city: string;
  country: string;
  emirate: Emirate;
}

interface DataWithBusinessInfo {
  data: BusinessInfo;
}

export const hasDataWithBusinessInfo = (
  entry: unknown
): entry is DataWithBusinessInfo =>
  isStringUnknownRecord(entry) &&
  isStringUnknownRecord(entry.data) &&
  typeof entry.data.tradeLicense === 'string' &&
  typeof entry.data.vatNumber === 'string' &&
  typeof entry.data.addressLine1 === 'string' &&
  typeof entry.data.addressLine2 === 'string' &&
  typeof entry.data.poBox === 'string' &&
  typeof entry.data.city === 'string' &&
  typeof entry.data.country === 'string' &&
  typeof entry.data.emirate === 'string' &&
  emiratesAsStrings.includes(entry.data.emirate);

/**
 * Orders Page RefundPage
 */

const BusinessSection = ({
  merchantData,
  isLoading,
  setIsSectionValid,
}: DefaultSectionProps) => {
  const { t } = useTranslation();

  const [isSaving, setIsSaving] = useState(false);
  const [ErrorToaster, setToasterError] = useToasterErrorState(setIsSaving);
  const [SuccessToaster, setToasterSuccess] = useToasterSuccessState();
  const [tradeLicense, setTradeLicense, tradeLicenseError] =
    useRequiredFieldState(merchantData?.tradeLicense ?? '', t);
  const [vatNumber, setVATNumber, vatNumberError] = useRequiredFieldState(
    merchantData?.vatNumber ?? '',
    t
  );
  const [addressLine1, setAddressLine1, addressLine1Error] =
    useRequiredFieldState(merchantData?.addressLine1 ?? '', t);
  const [addressLine2, setAddressLine2, addressLine2Error] =
    useRequiredFieldState(merchantData?.addressLine2 ?? '', t);
  const [city, setCity, cityError] = useRequiredFieldState(
    merchantData?.city ?? '',
    t
  );
  const [country, setCountry, countryError] = useRequiredFieldState(
    merchantData?.country ?? '',
    t
  );
  const [poBox, setPOBox, poBoxError] = useRequiredFieldState(
    merchantData?.poBox ?? '',
    t
  );
  const [emirate, setEmirate, emirateError] = useRequiredFieldState(
    merchantData?.emirate ?? '',
    t
  );

  useEffect(() => {
    setTradeLicense(merchantData?.tradeLicense ?? '');
    setVATNumber(merchantData?.vatNumber ?? '');
    setAddressLine1(merchantData?.addressLine1 ?? '');
    setAddressLine2(merchantData?.addressLine2 ?? '');
    setCity(merchantData?.city ?? '');
    setCountry(merchantData?.country ?? '');
    setPOBox(merchantData?.poBox ?? '');
    setEmirate(merchantData?.emirate ?? '');
  }, [merchantData]);

  const tradeLicenseOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setTradeLicense(e.target.value),
    [setTradeLicense]
  );
  const vatNumberOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setVATNumber(e.target.value),
    [setVATNumber]
  );
  const addressLine1OnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setAddressLine1(e.target.value),
    [setAddressLine1]
  );
  const addressLine2OnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setAddressLine2(e.target.value),
    [setAddressLine2]
  );
  const cityOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setCity(e.target.value),
    [setCity]
  );
  const countryOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setCountry(e.target.value),
    [setCountry]
  );
  const poBoxOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setPOBox(e.target.value),
    [setPOBox]
  );
  const emirateOnSelect = useCallback(
    (key: string) => {
      if (isEmirate(key)) {
        setEmirate(key);
        return;
      }
      setEmirate('');
    },
    [setEmirate]
  );
  const emirateDropdownData = useMemo(
    () =>
      emirates.map((key) => ({
        key,
        value: key || t('merchantOnboarding.business.field.emirate'),
      })),
    [t]
  );

  const haveErrors = useMemo(
    () =>
      tradeLicenseError.hasError ||
      vatNumberError.hasError ||
      addressLine1Error.hasError ||
      addressLine2Error.hasError ||
      cityError.hasError ||
      countryError.hasError ||
      poBoxError.hasError ||
      emirateError.hasError,
    [
      tradeLicenseError.hasError,
      vatNumberError.hasError,
      addressLine1Error.hasError,
      addressLine2Error.hasError,
      cityError.hasError,
      countryError.hasError,
      poBoxError.hasError,
      emirateError.hasError,
    ]
  );

  const saveOnClick = useCallback(() => {
    if (!isLoading && merchantData) {
      setIsSaving(true);
      api
        .patch({
          endpoint: merchantEndpoint(`${merchantData.id}`),
          params: {
            tradeLicense,
            vatNumber,
            addressLine1,
            addressLine2,
            poBox,
            city,
            country,
            emirate,
          },
        })
        .then(() => {
          setIsSaving(false);
          setToasterSuccess(
            t('onboardingForm.success.title.saving'),
            t('onboardingForm.success.message.saving')
          );
        })
        .catch((error: any) =>
          setToasterError(t('onboardingForm.error.title.saving'), error)
        );
    }
  }, [
    haveErrors,
    tradeLicense,
    vatNumber,
    addressLine1,
    addressLine2,
    poBox,
    city,
    country,
    emirate,
    setToasterSuccess,
  ]);

  useEffect(() => setIsSectionValid(!haveErrors), [haveErrors]);

  return (
    <OnboardingForm
      saveDisabled={haveErrors}
      isSaving={isSaving}
      saveOnClick={saveOnClick}
    >
      {(formClassname) => (
        <>
          <ErrorToaster />
          <SuccessToaster />
          <div className={`${formClassname}__line`}>
            <strong className={`${formClassname}__label-text`}>
              {t('merchantOnboarding.business.section.trade')}
            </strong>
          </div>
          <div className={`${formClassname}__line`}>
            <div className={`${formClassname}__col`}>
              <Input
                placeholder={t(
                  'merchantOnboarding.business.field.tradeLicense'
                )}
                name="tradeLicense"
                value={tradeLicense}
                onChange={tradeLicenseOnChange}
                error={tradeLicenseError.message}
              />
            </div>
            <div>
              <Input
                placeholder={t('merchantOnboarding.business.field.vatNumber')}
                name="vatNumber"
                value={vatNumber}
                onChange={vatNumberOnChange}
                error={vatNumberError.message}
              />
            </div>
          </div>
          <div className={`${formClassname}__separator`} />
          <div className={`${formClassname}__line`}>
            <strong className={`${formClassname}__label-text`}>
              {t('merchantOnboarding.business.section.address')}
            </strong>
          </div>
          <div className={`${formClassname}__line`}>
            <div className={`${formClassname}__col`}>
              <Input
                placeholder={t(
                  'merchantOnboarding.business.field.addressLine1'
                )}
                name="addressLine1"
                value={addressLine1}
                onChange={addressLine1OnChange}
                error={addressLine1Error.message}
              />
            </div>
            <div>
              <Input
                placeholder={t('merchantOnboarding.business.field.city')}
                name="city"
                value={city}
                onChange={cityOnChange}
                error={cityError.message}
              />
            </div>
          </div>
          <div className={`${formClassname}__line`}>
            <div className={`${formClassname}__col`}>
              <Input
                placeholder={t(
                  'merchantOnboarding.business.field.addressLine2'
                )}
                name="addressLine2"
                value={addressLine2}
                onChange={addressLine2OnChange}
                error={addressLine2Error.message}
              />
            </div>
            <div>
              <Input
                placeholder={t('merchantOnboarding.business.field.country')}
                name="country"
                value={country}
                onChange={countryOnChange}
                error={countryError.message}
              />
            </div>
          </div>
          <div className={`${formClassname}__line`}>
            <div className={`${formClassname}__col`}>
              <Input
                placeholder={t('merchantOnboarding.business.field.poBox')}
                name="poBox"
                value={poBox}
                onChange={poBoxOnChange}
                error={poBoxError.message}
              />
            </div>
            <div>
              <SelectComponent
                label={t('merchantOnboarding.business.field.emirate')}
                data={emirateDropdownData}
                selected={emirate}
                onSelect={emirateOnSelect}
                error={emirateError.message}
              />
            </div>
          </div>
        </>
      )}
    </OnboardingForm>
  );
};

export default BusinessSection;
